import React from 'react';
import ServerAddStore from './ServerAddStore';
import { observer } from 'mobx-react';
import { action, autorun, makeObservable } from 'mobx';
import { Button, Card, CardBody, FormGroup, Input, Label } from 'reactstrap';
import ErrorList from '~/components/ErrorList';
import HostnameOption from '~/components/size/HostnameOption';
import LicenseOptions from '~/components/size/LicenseOptions';
import NetworkSelector from '~/components/size/NetworkSelector';
import OSSelector from '~/components/size/OSSelector';
import RegionSelector from '~/components/size/RegionSelector';
import SizeOptionsEditor from '~/components/size/SizeOptions';
import SizeSelector from '~/components/size/SizeSelector';
import SizeTypeSelector from '~/components/size/SizeTypeSelector';
import SshKeyOptions from '~/components/size/SshKeysOption';
import UserDataOption from '~/components/size/UserDataOption';
import { getChildErrors, getErrorsFor, hasChildErrors } from '~/lib/helper/ErrorHelper';
import { supportsSshKeys, supportsUserData } from '~/lib/helper/OperatingSystemHelper';
import { hasIncludedBackups } from '~/lib/helper/SizeHelper';
import UnmountHelper from '~/lib/helper/UnmountHelper';
import './ServerAddForm.scss';
import { getAppContext } from '~/lib/AppContext';

interface IServerAddFormProps {
    store: ServerAddStore;
    forSignup?: boolean;
}

@observer
export default class ServerAddForm extends React.Component<IServerAddFormProps> {
    private readonly store = this.props.store;

    constructor(props: IServerAddFormProps) {
        super(props);
        makeObservable(this);
    }

    get showAdvanced() {
        return this.store.showAdvanced;
    }

    unmountHelper = new UnmountHelper(this);

    componentDidMount() {
        // We can't display the basic backup options form if any of the sizes include backups.
        // To avoid this we force the toggling of advanced when an included backup is detected in the list of sizes.
        const disposeReaction = autorun(() => {
            const sizesHaveIncludedBackups = Boolean(
                this.store.serverOptions.sizes.items.find((s) => hasIncludedBackups(s))
            );
            if (sizesHaveIncludedBackups && !this.showAdvanced) {
                this.toggleAdvanced();
            }
        });
        this.unmountHelper.onUnmount(disposeReaction);
    }

    renderFacilitiesAndOperatingSystems() {
        return (
            <div className="step">
                <div className="step-header">
                    <mark>1</mark> Select your location and operating system
                </div>
                <Card>
                    <CardBody>
                        {this.renderErrorsFor('region')}
                        <RegionSelector
                            regions={this.store.validFacilities}
                            selected={this.store.selectedRegion?.slug}
                            onRegionClick={(f) => this.store.selectRegion(f.slug!)}
                        />
                    </CardBody>
                    <CardBody>
                        {this.renderErrorsFor('image')}
                        <OSSelector
                            operatingSystems={this.store.validOperatingSystems}
                            selected={this.store.selectedOperatingSystem?.slug ?? undefined}
                            onOperatingSystemSelected={(os) => this.store.selectOperatingSystem(os?.id)}
                        />
                    </CardBody>
                </Card>
            </div>
        );
    }

    renderSizeTypesAndSizes() {
        const sizes = this.store.displayableSizesForSelectedType.slice();

        return (
            <div className="step">
                <div className="step-header">
                    <mark>2</mark> Choose your {getAppContext().config.productName} resources
                </div>
                <Card>
                    <CardBody>
                        <SizeTypeSelector
                            sizeTypes={this.store.validSizeTypes}
                            selected={this.store.selectedSizeType?.slug}
                            onSizeTypeSelected={(pt) => this.store.selectSizeType(pt.slug)}
                        />
                    </CardBody>
                    <CardBody>
                        {this.renderErrorsFor('size')}
                        <SizeSelector
                            sizes={sizes}
                            selectedOS={this.store.selectedOperatingSystem}
                            selectedSizeSlug={this.store.selectedSize?.slug ?? undefined}
                            selectedMemory={this.store.selectedSizeOptions?.memory ?? undefined}
                            selectedStorage={this.store.selectedSizeOptions?.disk ?? undefined}
                            selectedRegionSlug={this.store.selectedRegion?.slug}
                            selectedTransfer={this.store.selectedSizeOptions?.transfer ?? undefined}
                            onSizeSelect={(size) => this.store.selectSize(size.slug)}
                            onSelectedMemoryChange={(mem) => this.store.updateSelectedExtras({ memory: mem })}
                            onSelectedStorageChange={(disk) => this.store.updateSelectedExtras({ disk: disk })}
                            onSelectedTransferChange={(xfr) => this.store.updateSelectedExtras({ transfer: xfr })}
                        />
                    </CardBody>
                </Card>
            </div>
        );
    }

    renderLicenseSettings(): React.ReactNode {
        if (this.store.validSoftware.length === 0) return null;
        const licenseErrors: { [softwareId: string]: string[] } = {};

        // build licenseError lookup by softwareId
        if (this.store.errorRequest?.licenses) {
            const licenseRequest = this.store.errorRequest.licenses;
            const errors = getChildErrors(this.store.currentErrors, 'licenses');
            if (Object.keys(errors).length > 0) {
                licenseRequest.forEach((license, i) => {
                    const errs = errors[`[${i}]`];
                    if (errs) {
                        licenseErrors[license.software_id] = errs;
                    }
                });
            }
        }

        return (
            <CardBody>
                <LicenseOptions
                    errors={licenseErrors}
                    licenses={this.store.selectedLicenses}
                    software={this.store.validSoftware}
                    onLicensesChanged={(l) => this.store.selectLicenses(l)}
                />
            </CardBody>
        );
    }

    renderErrorsFor(field: string): JSX.Element {
        return <ErrorList errors={getErrorsFor(this.store.currentErrors, field)} />;
    }

    renderNetwork() {
        const networks = this.store.serverOptions.networks.items;
        if (networks.length === 0) return null;

        const elements = (
            <FormGroup>
                <Label>Network</Label>
                <NetworkSelector
                    networks={networks}
                    selected={this.store.selectedNetworkId}
                    onChange={(networkId) => this.store.selectNetwork(networkId)}
                ></NetworkSelector>
                {this.renderErrorsFor('network_id')}
                {this.renderErrorsFor('vpc_ipv4_address')}
            </FormGroup>
        );
        return (
            <CardBody className="NetworkSelector">
                <FormGroup style={{ visibility: this.store.selectedNetworkId ? 'visible' : 'hidden' }}>
                    <Label for="vpcIpv4Address">VPC IP</Label>
                    <Input
                        id="vpcIpv4Address"
                        type="text"
                        placeholder="Optional"
                        value={this.store.vpcIpv4Address}
                        onChange={(e) => this.store.setVpcIpv4Address(e.target.value)}
                    />
                </FormGroup>
                {elements}
                {this.store.selectedNetworkId && this.renderVpcConnectionType()}
            </CardBody>
        );
    }

    renderVpcConnectionType() {
        return (
            <FormGroup>
                <Label>VPC Public IP Connection</Label>
                <FormGroup check>
                    <Input
                        type="radio"
                        id="vpcConnectionNat"
                        name="vpcConnectionType"
                        checked={!this.store.separatePrivateNetworkInterface}
                        onChange={() => this.store.setSeparatePrivateNetworkInterface(false)}
                    />
                    <Label check for="vpcConnectionNat">
                        NAT (Public IP traffic is delivered via NAT)
                    </Label>
                </FormGroup>
                <FormGroup check>
                    <Input
                        type="radio"
                        id="vpcConnectionDedicated"
                        name="vpcConnectionType"
                        checked={this.store.separatePrivateNetworkInterface}
                        onChange={() => this.store.setSeparatePrivateNetworkInterface(true)}
                    />
                    <Label check for="vpcConnectionDedicated">
                        Dedicated Interface (Public IP traffic is on a dedicated interface)
                    </Label>
                </FormGroup>
                {this.renderErrorsFor('separate_private_network_interface')}
            </FormGroup>
        );
    }

    renderSshKeys() {
        if (!this.store.selectedOperatingSystem) return null;
        if (!supportsSshKeys(this.store.selectedOperatingSystem)) return null;

        const errors = getErrorsFor(this.store.currentErrors, 'ssh_keys') ?? [];

        if (hasChildErrors(this.store.currentErrors, 'ssh_keys')) {
            errors.push('Some of the SSH key selections are invalid');
        }

        const elements = (
            <SshKeyOptions
                keys={this.store.serverOptions.sshKeys}
                errors={errors}
                selectedIds={this.store.selectedSshKeysIds}
                onToggleSelectedId={(id) => this.store.toggleSelectedSshKey(id)}
            />
        );
        return <CardBody>{elements}</CardBody>;
    }

    renderHostName() {
        const elements = (
            <HostnameOption
                hostname={this.store.hostname}
                errors={getErrorsFor(this.store.currentErrors, 'name')}
                onChange={(name) => this.store.setHostname(name)}
                showAdvanced={this.store.showAdvanced}
            />
        );
        return <CardBody>{elements}</CardBody>;
    }

    @action
    toggleAdvanced() {
        this.store.showAdvanced = true;
        this.store.updateSelectedExtras({
            /* ensure daily, weekly and monthly are defined, this prevents the "you must select a backup option" error when using the advanced view */
            daily_backups: this.store.selectedSize?.options.daily_backups ?? 0,
            weekly_backups: this.store.selectedSize?.options.weekly_backups ?? 0,
            monthly_backups: this.store.selectedSize?.options.monthly_backups ?? 0,
            offsite_backups: false,
            ...this.store.selectedExtras,
        });
    }

    renderSizeOptionsSection() {
        if (!this.store.selectedSize) return null;
        if (!this.store.selectedSizeOptions) return null;
        return (
            <SizeOptionsEditor
                showAdvanced={this.showAdvanced}
                sizeSelections={this.store.selectedSizeOptions}
                size={this.store.selectedSize}
                onSizeSelectionsChange={(opts) => this.store.updateSelectedExtras(opts)}
                errors={getChildErrors(this.store.currentErrors, 'options')}
            ></SizeOptionsEditor>
        );
    }

    renderUserDataSettings() {
        if (!this.store.selectedOperatingSystem) return null;
        if (!supportsUserData(this.store.selectedOperatingSystem)) return null;

        return (
            <CardBody>
                <UserDataOption
                    userData={this.store.selectedUserData}
                    errors={getErrorsFor(this.store.currentErrors, 'user_data')}
                    onChange={(userData) => this.store.setUserData(userData)}
                />
            </CardBody>
        );
    }

    renderServerConfig() {
        if (!this.store.selectedSize) return null;

        return (
            <div className={`step ${this.showAdvanced ? 'advanced' : 'basic'}`}>
                <div className="step-header">
                    <mark>3</mark> Configure your server's settings
                    {!this.showAdvanced && (
                        <Button data-test-id="show-all" onClick={() => this.toggleAdvanced()}>
                            View All
                        </Button>
                    )}
                </div>
                <Card>
                    {this.renderHostName()}
                    {this.renderNetwork()}

                    {this.showAdvanced && (
                        <>
                            {this.renderSshKeys()}
                            {this.renderLicenseSettings()}
                        </>
                    )}
                    {this.renderSizeOptionsSection()}
                    {this.showAdvanced && this.renderUserDataSettings()}

                    {!this.showAdvanced && this.renderLicenseSettings()}
                </Card>
            </div>
        );
    }

    render() {
        return (
            <div className="serverAddForm">
                {this.renderFacilitiesAndOperatingSystems()}
                {this.renderSizeTypesAndSizes()}
                {this.renderServerConfig()}
            </div>
        );
    }
}
